import { ThemeEnum } from "@src/app/components//ThemeProvider/ThemeProvider";
import PageArticleList from "@src/app/components/ArticleList/PageArticleList";
import VideoPlayer from "@src/app/components/VideoPlayer/VideoPlayer";
import { useCustomParams } from "@src/app/hooks/useCustomParams";
import { useRouteResponse } from "@src/app/hooks/useRouteResponse";
import viewportState from "@src/app/stores/viewport";
import type { BFFArticleCard } from "@src/app/types/bff";
import { type ReactElement, useCallback, useEffect, useState } from "react";

export default function VideoArticle(): ReactElement {
  const res = useRouteResponse("articlePage");
  const { articles = [] } = res;
  const { isMobileView } = viewportState;
  const { level2 } = useCustomParams();

  // State management
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(0);
  const [videoArticles, setVideoArticles] = useState(articles);
  const [loadMore, setLoadMore] = useState(false);
  const [isVideoError, setIsVideoError] = useState(false);

  // Get selected video details
  const selectedVideoArticle = videoArticles[selectedVideoIndex];

  // Handlers for video navigation
  const handleNextVideo = useCallback(() => {
    // Find next valid video (skip videos with empty youtubeUrl)
    let nextIndex = selectedVideoIndex + 1;
    while (nextIndex < videoArticles.length) {
      const nextVideo = videoArticles[nextIndex];
      if (nextVideo.youtubeUrl && nextVideo.youtubeUrl.trim() !== "") {
        break; // Found valid video
      }
      nextIndex++;
    }

    // If found a valid video, switch to it
    if (nextIndex < videoArticles.length) {
      setSelectedVideoIndex(nextIndex);
      // Trigger load more on mobile view if approaching last video
      if (isMobileView && nextIndex === videoArticles.length - 1) {
        setLoadMore(true);
      }
    }
  }, [selectedVideoIndex, videoArticles, isMobileView]);

  const handlePreviousVideo = () => {
    // Find previous valid video (skip videos with empty youtubeUrl)
    let previousIndex = selectedVideoIndex - 1;
    while (
      previousIndex >= 0 &&
      !videoArticles[previousIndex].youtubeUrl?.trim()
    ) {
      previousIndex--;
    }

    if (previousIndex >= 0) {
      setSelectedVideoIndex(previousIndex);
    }
  };

  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
    setIsVideoPaused(false);
    handleNextVideo();
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
    setIsVideoPaused(false);
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
    setIsVideoPaused(true);
  };

  const handleVideoArticles = (data: BFFArticleCard[]) => {
    setVideoArticles(data);
  };

  useEffect(() => {
    // Update browser URL without reloading the page when a new video is selected
    if (typeof window !== "undefined") {
      const currentQuery = window.location.search; // keep current query parameters
      window.history.replaceState(
        null,
        "",
        `${selectedVideoArticle.urlPath}${currentQuery}`,
      );
    }

    // If the selected video index is not 0, validate the YouTube URL
    // and handle video error if it occurs for desktop view
    if (
      selectedVideoIndex !== 0 &&
      selectedVideoIndex + 1 < videoArticles.length
    ) {
      // Validate YouTube URL
      if (selectedVideoArticle.youtubeUrl?.trim() === "") {
        console.error("Invalid YouTube URL for the selected video article.");
        setIsVideoError(true);
      }

      // Handle video error by skipping to the next video
      if (isVideoError) {
        handleNextVideo();
        setIsVideoError(false);
      }
    }
  }, [
    selectedVideoArticle,
    selectedVideoIndex,
    videoArticles.length,
    isVideoError,
    isMobileView,
    handleNextVideo,
  ]);

  return (
    <div className="bg-inverse">
      <div className="mx-auto max-w-screen-xxl px-0 lg:px-[24px] xxl:px-[120px]">
        <VideoPlayer
          videoArticle={selectedVideoArticle}
          totalVideos={videoArticles.length}
          isAutoplay={
            selectedVideoIndex + 1 == videoArticles.length ? false : true
          }
          selectedVideoIndex={selectedVideoIndex}
          onPreviousVideo={handlePreviousVideo}
          onNextVideo={handleNextVideo}
          onEndedVideo={handleVideoEnd}
          onPlayVideo={handleVideoPlay}
          onPausedVideo={handleVideoPause}
          onErrorVideo={() => {
            setIsVideoError(true);
          }}
          showIndicator={false}
        />
      </div>

      {/* Articles */}
      <PageArticleList
        initialData={res.articles || []}
        theme={ThemeEnum.dark}
        title="所有视频"
        requestPath={`/videos/${level2}`}
        selectedArticleId={selectedVideoArticle.id}
        isVideoPlaying={isVideoPlaying}
        isVideoPaused={isVideoPaused}
        updatedData={handleVideoArticles}
        loadMore={loadMore}
        showSubHeader={false}
      />
    </div>
  );
}
